/**
 * What Teamna sends to Sentry, for the API (`@teamna/core/lib/sentry.ts`) and Desk web
 * (`apps/desk-web/src/lib/sentry.ts`). Structural types only, so this package needs no Sentry.
 *
 * Two rules. First, no personal data: a Teamna request can carry children's names, phone numbers
 * and payments, so `SENTRY_DATA_COLLECTION` turns off everything the SDK collects by default (since
 * SDK v11 the default is to collect it all). Second, no secrets in URLs: some paths carry an invite
 * code (`/join/<code>`, `/team-invite/<token>`), a family code (`/find/<code>`) or a payment token
 * (`/pay/<token>`), and a sign-in token travels in the query or the fragment
 * (`/email-sign-in#token=...`). These parts are removed before an event or a span leaves. Error
 * text can also carry them (a fetch error with its URL, a message with a phone number), so the
 * messages of an event are cleaned too.
 */

/**
 * The SDK's `dataCollection` option. No user info (IP address), no cookies, no bodies, no query
 * strings, no SQL parameters, no local variables, no AI prompts or answers. Headers: the user agent
 * only, which gives the browser and the OS of an error.
 */
export const SENTRY_DATA_COLLECTION = {
  userInfo: false,
  cookies: false,
  httpHeaders: { request: { allow: ["user-agent"] }, response: false },
  httpBodies: [],
  urlQueryParams: false,
  graphQL: { document: false, variables: false },
  genAI: { inputs: false, outputs: false },
  mcp: { inputs: false, outputs: false },
  databaseQueryData: false,
  queues: false,
  stackFrameVariables: false,
};

/** Path segments whose next segment is a secret, also under `/v1`. */
const SECRET_BEFORE = new Set(["join", "team-invite", "find", "pay"]);

export const REDACTED = "[redacted]";

/**
 * The URL with no query, no fragment, and `[redacted]` for the segment after a secret prefix. Works
 * on a full URL and on a path; anything else is returned as it is.
 */
export function redactSecretUrl(url: string): string {
  const cut = url.search(/[?#]/);
  const base = cut === -1 ? url : url.slice(0, cut);
  const parts = base.split("/");
  for (let i = 0; i < parts.length - 1; i += 1) {
    if (SECRET_BEFORE.has(parts[i] ?? "") && parts[i + 1]) parts[i + 1] = REDACTED;
  }
  return parts.join("/");
}

/**
 * A full URL, or a path inside free text: a `/` that does not follow a letter, a digit, a dot or
 * another `/`, up to a space, a quote or a bracket. A bracket ends it so that `[redacted]` from an
 * earlier pass is not cleaned again. `redactSecretUrl` then removes its query,
 * its fragment and a secret segment, and leaves the rest as it is.
 */
const URL_IN_TEXT = /https?:\/\/[^\s"'<>`()[\]]+|(?<![\w./])\/[^\s"'<>`()[\]]+/g;
/**
 * A secret path segment anywhere, also in a URL with no scheme (`desk.teamna.app/v1/pay/<token>`)
 * or in brackets (`/join/([CODE])`), brackets included. A segment that is already `[redacted]` is left.
 */
const SECRET_SEGMENT_IN_TEXT = new RegExp(
  `/(${[...SECRET_BEFORE].join("|")})/[(\\[]*(?!redacted\\])[^\\s/?#"'<>\`()[\\]]+[)\\]]*`,
  "g",
);
/**
 * A query or a fragment that the URL match did not take: joined to a word (`app/x?token`) or with a
 * value (`?code=1`).
 */
const QUERY_IN_TEXT = /(?<=\S)[?#][^\s"'<>`)\]]+|[?#][^\s"'<>`=)\]]*=[^\s"'<>`)\]]*/g;
/** An email address inside free text. */
const EMAIL_IN_TEXT = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g;
/**
 * A phone number inside free text: international with a `+` (`+971501234567`, `+971 (50) 123-4567`)
 * or UAE local (`0501234567`, `050 123 4567`, `(050) 123-4567`, `04-123-4567`). Not dates, times, IP
 * addresses or ids.
 */
const PHONE_IN_TEXT = /\+\d{1,3}[\s.-]?\(?\d{1,4}\)?(?:[\s.-]?\d{2,4}){2,4}|\(?\b0\d{1,2}\)?[\s-]?\d{3}[\s-]?\d{4}\b/g;

/**
 * The text attributes of a DOM selector, as the browser SDK writes it for a click, INP, LCP or CLS
 * span (`div.list > button.row[aria-label="Mark Layan present"]`). A Desk label can be a child's
 * name, and the SDK does not escape it, so no end of the value can be trusted: everything from the
 * first text attribute to the end of the text is removed. The elements before it stay.
 */
const SELECTOR_TEXT_IN_TEXT = /\[(aria-label|title|alt|name)="[\s\S]*$/;
/** An IPv4 address inside free text. */
const IPV4_IN_TEXT = /\b(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})\b/g;
/** A run of hex digits and colons inside free text; `redactIpv6` finds the IPv6 address in it. */
const HEX_COLON_RUN = /(?<![0-9a-f:])[0-9a-f:]{3,}(?![0-9a-f:])/gi;

/** Loopback and private networks: an address there is a server, never a person. */
function isPrivateIpv4(a: number, b: number): boolean {
  return a === 127 || a === 10 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168);
}

/** Strict IPv6: groups of 1 to 4 hex digits, eight of them, or fewer with one `::`. */
function isIpv6(text: string): boolean {
  const halves = text.split("::");
  if (halves.length > 2) return false;
  const groups = halves.flatMap((half) => (half === "" ? [] : half.split(":")));
  if (!groups.every((group) => /^[0-9a-f]{1,4}$/i.test(group))) return false;
  return halves.length === 2 ? groups.length < 8 : groups.length === 8;
}

/**
 * The run with its IPv6 address replaced. The address can follow a label and a colon
 * (`addr:2606:4700::1111`), so each start after a colon is tried, longest first. A time
 * (`08:00:00`) is not IPv6. `::1` is loopback and stays.
 */
function redactIpv6(run: string): string {
  for (let i = 0; i < run.length; i += 1) {
    if (i > 0 && run[i - 1] !== ":") continue;
    const tail = run.slice(i);
    if (isIpv6(tail)) return tail === "::1" ? run : `${run.slice(0, i)}${REDACTED}`;
  }
  return run;
}

/**
 * Free text (an error message, a breadcrumb message) with the secrets of every URL in it removed,
 * and `[redacted]` for each email address, phone number, public IP address and DOM selector label.
 * Other names cannot be found this way: the code must not put a person's name in an error message.
 */
export function redactText(text: string): string {
  return text
    .replace(SELECTOR_TEXT_IN_TEXT, `[$1="${REDACTED}"]`)
    .replace(URL_IN_TEXT, (url) => redactSecretUrl(url))
    .replace(SECRET_SEGMENT_IN_TEXT, `/$1/${REDACTED}`)
    .replace(QUERY_IN_TEXT, "")
    .replace(EMAIL_IN_TEXT, REDACTED)
    .replace(PHONE_IN_TEXT, REDACTED)
    .replace(IPV4_IN_TEXT, (ip, a: string, b: string) => (isPrivateIpv4(Number(a), Number(b)) ? ip : REDACTED))
    .replace(HEX_COLON_RUN, redactIpv6);
}

/** A URL, a route or a span name: its query, fragment and secret segment go, then personal text. */
function redactUrl(url: string): string {
  return redactText(redactSecretUrl(url));
}

type Data = Record<string, unknown> | undefined;

/** The parts of a Sentry event that can hold a URL or personal text. Structural, so this package needs no Sentry. */
export type RedactableEvent = {
  message?: string;
  logentry?: { message?: string; formatted?: string };
  exception?: { values?: Array<{ value?: string }> };
  request?: { url?: string; query_string?: unknown };
  transaction?: string;
  breadcrumbs?: Array<{ message?: string; data?: Data }>;
  spans?: Array<{ description?: string; data?: Data }>;
  contexts?: { trace?: { data?: Data } };
};

/** Data keys the Sentry SDKs fill with a URL or a path. */
const URL_KEYS = new Set(["url", "from", "to", "url.full", "url.path", "http.url", "http.target", "http.route"]);
/** Data keys that hold only a query or a fragment (`url.query`, `url.fragment`, `http.query`, ...). */
const QUERY_KEY = /(^|\.)(query|fragment|search|hash)$/;

/**
 * Cleans breadcrumb, span or trace data in place, at every depth (console breadcrumbs keep their
 * arguments as an array): a query or fragment key is deleted, a URL key loses its query, fragment
 * and secret segment, and every text value goes through `redactText`. A streamed span can hold a
 * value as `{ value, type }`, which the walk reaches as a nested object.
 */
function redactData(data: Data): void {
  if (data) redactTree(data, new WeakSet());
}

function redactTree(node: Record<string, unknown> | unknown[], seen: WeakSet<object>): void {
  if (seen.has(node)) return;
  seen.add(node);
  const entries: Array<[string | number, unknown]> = Array.isArray(node) ? node.map((value, i) => [i, value]) : Object.entries(node);
  for (const [key, value] of entries) {
    if (typeof key === "string" && QUERY_KEY.test(key)) {
      delete (node as Record<string, unknown>)[key];
    } else if (typeof value === "string") {
      (node as Record<string | number, unknown>)[key] = typeof key === "string" && URL_KEYS.has(key) ? redactUrl(value) : redactText(value);
    } else if (value && typeof value === "object") {
      if (typeof key === "string" && URL_KEYS.has(key) && typeof (value as { value?: unknown }).value === "string") {
        (value as { value: string }).value = redactUrl((value as { value: string }).value);
      }
      redactTree(value as Record<string, unknown>, seen);
    }
  }
}

/**
 * Removes the secrets from every URL and the emails and phone numbers from every message of a
 * Sentry error event, in place. The exception values include each linked `cause`. For `beforeSend`.
 */
export function redactEvent<T extends RedactableEvent>(event: T): T {
  if (event.message) event.message = redactText(event.message);
  if (event.logentry?.message) event.logentry.message = redactText(event.logentry.message);
  if (event.logentry?.formatted) event.logentry.formatted = redactText(event.logentry.formatted);
  for (const value of event.exception?.values ?? []) {
    if (value.value) value.value = redactText(value.value);
  }
  if (event.request) {
    if (event.request.url) event.request.url = redactUrl(event.request.url);
    delete event.request.query_string;
  }
  if (event.transaction) event.transaction = redactUrl(event.transaction);
  for (const crumb of event.breadcrumbs ?? []) {
    if (crumb.message) crumb.message = redactText(crumb.message);
    redactData(crumb.data);
  }
  for (const span of event.spans ?? []) {
    if (span.description) span.description = redactUrl(span.description);
    redactData(span.data);
  }
  redactData(event.contexts?.trace?.data);
  return event;
}

/** The shape of a streamed Sentry span (SDK v11 sends spans one by one). */
export type RedactableSpan = { name: string; attributes: Record<string, unknown> };

/** Removes the secrets from a span's name and URL attributes, in place. For `beforeSendSpan`. */
export function redactSpan<T extends RedactableSpan>(span: T): T {
  span.name = redactUrl(span.name);
  redactData(span.attributes);
  return span;
}

/**
 * Breadcrumbs that can carry a name, so they never leave: `console` (the server logger writes its
 * fields to the console: names, phone numbers) and `ui.*` (a click or an input names the element by
 * its `aria-label`, `title` or `alt`, and a Desk button can carry a child's name). Navigation, fetch
 * and HTTP breadcrumbs stay, cleaned by `redactEvent`. For `beforeBreadcrumb`; both starts also turn
 * off the integrations that make them.
 */
export function dropPersonalBreadcrumb<T extends { category?: string }>(crumb: T): T | null {
  const category = crumb.category ?? "";
  return category === "console" || category.startsWith("ui.") ? null : crumb;
}
